import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { PixelService } from '../../../../core/services/pixel.service';
import { AnimatePresence, motion } from 'framer-motion';
// import CodeSnippet from '../components/CodeSnippet';
import PlatformSnippetTabs from '../components/PlatformSnippetTabs';
// import { webPixelSnippet, sdkSetupSnippet, serverSetupSnippet } from './platformSnippets';
import { setupOptions } from './platformSnippets';

interface Props {
    UserReducer: any;
    existingPixel: any;
    platforms?: string[];
    isReadOnly?: boolean;
    onGenerated: (pixelData: any) => void;
}

export const Step1GeneratePixel: React.FC<Props> = ({ UserReducer, existingPixel, platforms, isReadOnly, onGenerated }) => {
    const [websiteUrl, setWebsiteUrl] = useState(
        existingPixel?.website_url || UserReducer?.website || ''
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // const [copied, setCopied] = useState(false);
    const [pixel, setPixel] = useState<any>(existingPixel);

    // Web pixel install snippet moved to platformSnippets.ts (webPixelSnippet)
    // so all three setup paths render through the same tabbed code+guide layout.
    // const generateSnippet = (cctm_id: string) => `<!-- ClanConnect Affiliate Pixel -->
    // <script>
    //   !function(w,d,s,l,i){
    //     w[l]=w[l]||[];
    //     var f=d.getElementsByTagName(s)[0],j=d.createElement(s);
    //     j.async=true;
    //     j.src='https://s3.ap-south-1.amazonaws.com/new-clanconnect.ai/affiliate/affiliate.js?id='+i;
    //     f.parentNode.insertBefore(j,f);
    //   }(window,document,'script','cctLayer','${'${cctm_id}'}');
    //   window.cctLayer=window.cctLayer||[];
    //   function cctag(){cctLayer.push(arguments);}
    //   cctag('js',new Date());
    //   cctag('config','${'${cctm_id}'}');
    // </script>
    // <!-- End ClanConnect Affiliate Pixel -->`;

    const handleGenerate = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await PixelService.generatePixel({
                user_id: UserReducer.user_id,
                account_id: UserReducer.account_id,
                influencer_id: UserReducer.influencer_id,
                brand_id: UserReducer.brand_id,
                website_url: websiteUrl,
                // All integration types are offered to every brand now —
                // record all three; the column is informational metadata only.
                // platforms: platforms && platforms.length > 0 ? platforms : ['web'],
                platforms: ['web', 'app', 'server'],
            });
            if (res?.data?.rows?.length > 0) {
                const row = res.data.rows[0];
                setPixel(row);
                // Do NOT advance yet — user must click "I've added this to my site → Next"
            } else {
                setError('Failed to generate pixel. Please try again.');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Copy handling now lives inside PlatformSnippetTabs (per-tab copy state)
    // const handleCopy = () => {
    //     if (!pixel?.cctm_id) return;
    //     navigator.clipboard.writeText(generateSnippet(pixel.cctm_id));
    //     setCopied(true);
    //     setTimeout(() => setCopied(false), 2500);
    // };

    return (
        <>
        <AnimatePresence mode="wait">
    <motion.div
        // key={activeStep}
        initial={{
            opacity: 0,
            y: 30,
        }}
        animate={{
            opacity: 1,
            y: 0,
        }}
        exit={{
            opacity: 0,
            y: -30,
        }}
        transition={{
            duration: 0.35,
        }}
    >
        
            {/* <Box>
                <Typography variant="h6" fontWeight={600} mb={1}>
                    Step 1 — Generate Your Pixel
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    We'll generate a unique pixel ID for your brand. Paste this script inside the{' '}
                    <code>&lt;head&gt;</code> tag of <strong>every page</strong> on your website — just once.
                </Typography>

                {!pixel && (
                    <Box mb={3} display="flex" flexDirection="column" gap={2} maxWidth={480}>
                        <TextField
                            label="Your website URL"
                            placeholder="https://yourbrand.com"
                            size="small"
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            fullWidth
                        />
                        {error && <Alert severity="error">{error}</Alert>}
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={loading}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Generate My Pixel'}
                        </Button>
                    </Box>
                )}

                {pixel?.cctm_id && (
                    <Box>
                        <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
                            Pixel ID: <strong>{pixel.cctm_id}</strong> — generated successfully.
                        </Alert>

                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                            Copy and paste this into your website's <code>&lt;head&gt;</code>:
                        </Typography>

                        <Box
                            component="pre"
                            sx={{
                                bgcolor: '#1e1e1e',
                                color: '#d4d4d4',
                                borderRadius: 1.5,
                                p: 2,
                                fontSize: '0.75rem',
                                overflowX: 'auto',
                                whiteSpace: 'pre',
                                fontFamily: 'monospace',
                                mb: 2,
                            }}
                        >
                            {generateSnippet(pixel.cctm_id)}
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Button
                                variant="outlined"
                                startIcon={copied ? <CheckCircleOutlineIcon color="success" /> : <ContentCopyIcon />}
                                onClick={handleCopy}
                                sx={{ textTransform: 'none' }}
                            >
                                {copied ? 'Copied!' : 'Copy Snippet'}
                            </Button>
                            {!isReadOnly && (
                                <Button
                                    variant="contained"
                                    onClick={() => onGenerated(pixel)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    I've added this to my site → Next
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box> */}
            <Box>
    {/* Header */}
    <Box mb={2} >
        <Typography
            variant="h6"
            fontWeight={700}
            mb={1}
        >
            Step 1 — Generate Your Pixel
        </Typography>

        <Typography
            color="text.secondary"
            sx={{ maxWidth: 700, fontSize: '0.875rem' }}
        >
            {/* Generate a unique tracking pixel for your website.
            Install it once in your site's
            <code> &lt;head&gt; </code>
            section and start tracking affiliate conversions instantly. */}
            Generate a unique tracking pixel for your brand. Install the script
            once in your website's
            <code> &lt;head&gt; </code>
            section — and if you also sell through a mobile app or want to send
            events from your backend, use the SDK and API keys below.
        </Typography>
    </Box>

    {!pixel && (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                background: '#fff',
                maxWidth: 600,
            }}
        >

            <TextField
                fullWidth
                label="Website URL"
                placeholder="https://yourstore.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                className='input-common input-style'
                sx={{ mb: 2 }}
            />

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                >
                    {error}
                </Alert>
            )}

            <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                sx={{
                    borderRadius: 999,
                    px: 4,
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 700,
                    background:
                        'linear-gradient(135deg,#2563EB,#7C3AED)',
                }}
            >
                {loading ? (
                    <CircularProgress
                        size={18}
                        sx={{ color: '#fff' }}
                    />
                ) : (
                    'Generate Pixel'
                )}
            </Button>
        </Paper>
    )}

    {pixel?.cctm_id && (
        <>
            {/* Success Card */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2.5,
                    borderRadius: 4,
                    bgcolor: '#ECFDF5',
                    border: '1px solid #BBF7D0',
                    mb: 2,
                }}
            >
                <CheckCircleOutlineIcon
                    sx={{
                        color: '#22C55E',
                        fontSize: 28,
                    }}
                />

                <Box>
                    <Typography fontWeight={700}>
                        Pixel Generated Successfully
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Pixel ID: {pixel.cctm_id}
                    </Typography>
                </Box>
            </Box>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1}
            >
                Installation Code
            </Typography>

            {/* VS Code Style Block */}
            {/* <Box
                sx={{
                    overflow: 'hidden',
                    borderRadius: 4,
                    bgcolor: '#0F172A',
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box display="flex" gap={1}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#EF4444',
                            }}
                        />
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#F59E0B',
                            }}
                        />
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#10B981',
                            }}
                        />
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{
                            color: '#9CA3AF',
                        }}
                    >
                        affiliate-pixel.js
                    </Typography>

                    <Button
                        size="small"
                        startIcon={
                            copied ? (
                                <CheckCircleOutlineIcon />
                            ) : (
                                <ContentCopyIcon />
                            )
                        }
                        onClick={handleCopy}
                        sx={{
                            color: '#fff',
                            textTransform: 'none',
                        }}
                    >
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                </Box>

                <Box
                    component="pre"
                    sx={{
                        p: 3,
                        color: '#E5E7EB',
                        fontSize: '0.8rem',
                        overflowX: 'auto',
                        m: 0,
                        lineHeight: 1.7,
                        fontFamily:
                            'SFMono-Regular,Consolas,Monaco,monospace',
                    }}
                >
                    {generateSnippet(pixel.cctm_id)}
                </Box>
            </Box> */}
            {/* Framework picker — HTML | WordPress | Shopify | … | PHP | REST API */}
            {/* <PlatformSnippetTabs
                snippets={{
                    web: webPixelSnippet(pixel),
                    app: sdkSetupSnippet(pixel),
                    server: serverSetupSnippet(pixel),
                }}
            /> */}
            <PlatformSnippetTabs options={setupOptions(pixel)} />

            {/* API keys — used by the Mobile App / Server tabs above */}
            <Box
                sx={{
                    p: 2.5,
                    borderRadius: 4,
                    bgcolor: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    my: 2,
                }}
            >
                <Typography fontWeight={700} mb={1}>
                    Your API Keys
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Publishable key (mobile apps): <strong>{pixel.publishable_key}</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} mb={1}>
                    Secret key (server only): <strong>{pixel.secret_key}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    The publishable key is safe to embed in your app. The secret key must
                    stay on your server — never put it in app or website code.
                </Typography>
            </Box>
            {/* Footer Actions */}
            <Box
                display="flex"
                gap={2}
                flexWrap="wrap"
            >
                {!isReadOnly && (
                    <Button
                        variant="contained"
                        onClick={() => onGenerated(pixel)}
                        sx={{
                            borderRadius: 999,
                            px: 4,
                            py: 1.2,
                            fontWeight: 700,
                            textTransform: 'none',
                            background:
                                'linear-gradient(135deg,#2563EB,#7C3AED)',
                        }}
                    >
                        I've added this to my site → Next
                    </Button>
                )}
            </Box>
        </>
    )}
</Box>
</motion.div>
        </AnimatePresence>
        </>
    );
};
